import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Tabs, TabsContent } from '@/components/ui/tabs'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { UserAvatarDropdown } from '@/components/ui/user-avatar-dropdown'
import { ChatPane } from '@/components/conversations/ChatPane'
import { PreviewPane } from '@/components/conversations/PreviewPane'
import { Bot, MessageSquare, Monitor, FileText, History, Plug, Globe } from 'lucide-react'
import { Logo } from '@/components/logo'
import type { Conversation, Message, GroupedTodos, Project, HistoryEntry, Pagination, OptimisticAttachment, ActiveWorkflow } from '@/types/conversations'

import type { CreditPack, OrganizationBilling } from '@/types/conversations'

interface SelectedElementContext {
  selector?: string
  cayuSelector?: string
  tag?: string
  text?: string
  route?: string
  appType?: 'web' | 'mobile'
  source?: 'canvas' | 'preview'
  elements?: Array<{ id: string; type: string; text?: string }>
}

interface MobileConversationViewProps {
  conversation: Conversation
  project: Project
  isProvisioning?: boolean
  historyEntries?: HistoryEntry[]
  historyPagy?: Pagination
  messages: Message[]
  groupedTodos: GroupedTodos
  processing: boolean
  currentActivity: string | null
  activityHistory?: string[]
  isSendingMessage: boolean
  isAcceptingChanges?: boolean
  isDiscardingChanges?: boolean
  isInterrupting?: boolean
  onSendMessage: (content: string, signedBlobIds?: string[], selectedElementContext?: SelectedElementContext, optimisticAttachments?: OptimisticAttachment[]) => Promise<void>
  onToggleMode: () => void
  onSwitchToMode?: (mode: string) => Promise<void>
  onInterrupt?: () => Promise<void>
  onApprovePlan: (messageId: number) => Promise<void>
  onSubmitAnswers: (messageId: number, answers: Record<string, string | string[]>) => Promise<void>
  onConfirmButton: (messageId: number, prompt: string | undefined, clickedLabel?: string) => Promise<void>
  onAcceptChanges?: () => Promise<void>
  onDiscardChanges?: () => Promise<void>
  organization?: OrganizationBilling
  creditPacks?: CreditPack[]
  nextStepSuggestions?: Array<{ name: string; prompt: string }>
  collapsedMessageIds?: Set<number>
  expandedTurns?: Set<number>
  onToggleTurnExpansion?: (userMessageId: number) => void
  activeWorkflow?: ActiveWorkflow | null
  onAbortWorkflow?: () => void
  onRetryWorkflow?: () => Promise<void>
  hasMoreMessages?: boolean
  isLoadingOlder?: boolean
  onLoadOlderMessages?: () => void
  onRemoveMobile?: () => Promise<void>
  sketchPane?: React.ReactNode
}

export function MobileConversationView({
  conversation,
  project,
  isProvisioning = false,
  historyEntries = [],
  historyPagy,
  messages,
  groupedTodos,
  processing,
  currentActivity,
  activityHistory = [],
  isSendingMessage,
  isAcceptingChanges = false,
  isDiscardingChanges = false,
  isInterrupting = false,
  onSendMessage,
  onToggleMode,
  onSwitchToMode,
  onInterrupt,
  onApprovePlan,
  onSubmitAnswers,
  onConfirmButton,
  organization,
  creditPacks = [],
  nextStepSuggestions = [],
  collapsedMessageIds,
  expandedTurns,
  onToggleTurnExpansion,
  activeWorkflow,
  onAbortWorkflow,
  onRetryWorkflow,
  hasMoreMessages,
  isLoadingOlder,
  onLoadOlderMessages,
  onRemoveMobile,
  sketchPane,
}: MobileConversationViewProps) {
  const { t } = useTranslation('conversations')
  const [activeTab, setActiveTab] = useState('chat')
  const supportsLiveEnvironment = project.capabilities.live_environment
  const isManagedAgentProject = project.project_type === 'managed_agent'
  const previewTabTitle = isManagedAgentProject ? t('preview.managedAgent.title') : t('preview.title')
  const showProductSpecTab = !isManagedAgentProject
  const showIntegrationsTab = !isManagedAgentProject
  // Track which tabs have been visited - mount on first access, stay mounted
  const [visitedTabs, setVisitedTabs] = useState<Set<string>>(new Set(['chat']))

  const handleTabChange = (tab: string) => {
    setActiveTab(tab)
    setVisitedTabs(prev => {
      if (prev.has(tab)) return prev
      const next = new Set(prev)
      next.add(tab)
      return next
    })
  }

  useEffect(() => {
    const tabUnavailable =
      (!supportsLiveEnvironment && activeTab === 'live') ||
      (!showProductSpecTab && activeTab === 'spec') ||
      (!showIntegrationsTab && activeTab === 'integrations')

    if (tabUnavailable) {
      handleTabChange('preview')
    }
  }, [activeTab, supportsLiveEnvironment, showProductSpecTab, showIntegrationsTab])

  return (
    <div className="flex h-dvh flex-col bg-content-bg overflow-hidden">
      {/* Mobile Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile Header */}
        <header className="flex shrink-0 flex-col bg-content-bg border-b">
          <div className="flex h-12 items-center justify-between px-4">
            <div className="flex items-center gap-2 min-w-0">
              <Logo iconOnly className="h-5 w-5 shrink-0" />
              <h1 className="font-semibold text-foreground truncate">{project.name}</h1>
            </div>
            <UserAvatarDropdown
              projectId={project.id}
              projectName={project.name}
              organization={organization}
            />
          </div>
          <div className="flex items-center px-4 pb-2">
          <Select value={activeTab} onValueChange={handleTabChange}>
            <SelectTrigger className="w-[140px] h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="chat">
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  <span>{t('tabs.chat')}</span>
                </div>
              </SelectItem>
              <SelectItem value="preview">
                <div className="flex items-center gap-2">
                  {isManagedAgentProject ? <Bot className="h-4 w-4" /> : <Monitor className="h-4 w-4" />}
                  <span>{previewTabTitle}</span>
                </div>
              </SelectItem>
              {showProductSpecTab && (
                <SelectItem value="spec">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    <span>{t('productSpec.title')}</span>
                  </div>
                </SelectItem>
              )}
              <SelectItem value="history">
                <div className="flex items-center gap-2">
                  <History className="h-4 w-4" />
                  <span>{t('tabs.history')}</span>
                </div>
              </SelectItem>
              {showIntegrationsTab && (
                <SelectItem value="integrations">
                  <div className="flex items-center gap-2">
                    <Plug className="h-4 w-4" />
                    <span>{t('tabs.integrations')}</span>
                  </div>
                </SelectItem>
              )}
              {supportsLiveEnvironment && (
                <SelectItem value="live">
                  <div className="flex items-center gap-2">
                    <span className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${
                      project.production_status === 'deployed' ? 'bg-green-500'
                        : project.production_status === 'deploy_failed' ? 'bg-red-500'
                        : project.production_status === 'deploying' ? 'bg-blue-500 animate-pulse'
                        : project.production_status === 'suspended_no_credits' ? 'bg-amber-500'
                        : 'bg-gray-400'
                    }`} />
                    <Globe className="h-4 w-4" />
                    <span>{t('tabs.live')}</span>
                  </div>
                </SelectItem>
              )}
            </SelectContent>
          </Select>
          </div>
        </header>

        {/* Tab Content */}
        <div className="flex-1 overflow-hidden">
          <TabsContent value="chat" className="h-full m-0 data-[state=inactive]:hidden" forceMount>
            {visitedTabs.has('chat') && (
              <div className="h-full w-full max-w-3xl mx-auto">
                <ChatPane
                  conversation={conversation}
                  project={project}
                  messages={messages}
                  groupedTodos={groupedTodos}
                  processing={processing}
                  currentActivity={currentActivity}
                  activityHistory={activityHistory}
                  isSendingMessage={isSendingMessage}
                  isAcceptingChanges={isAcceptingChanges}
                  isDiscardingChanges={isDiscardingChanges}
                  isInterrupting={isInterrupting}
                  onSendMessage={onSendMessage}
                  onToggleMode={onToggleMode}
                  onSwitchToMode={onSwitchToMode}
                  onInterrupt={onInterrupt}
                  onApprovePlan={onApprovePlan}
                  onSubmitAnswers={onSubmitAnswers}
                  onConfirmButton={onConfirmButton}
                  nextStepSuggestions={nextStepSuggestions}
                  collapsedMessageIds={collapsedMessageIds}
                  expandedTurns={expandedTurns}
                  onToggleTurnExpansion={onToggleTurnExpansion}
                  organization={organization}
                  creditPacks={creditPacks}
                  onAbortWorkflow={onAbortWorkflow}
                  onRetryWorkflow={onRetryWorkflow}
                  hasMoreMessages={hasMoreMessages}
                  isLoadingOlder={isLoadingOlder}
                  onLoadOlderMessages={onLoadOlderMessages}
                />
              </div>
            )}
          </TabsContent>

          <TabsContent value="preview" className="h-full m-0 data-[state=inactive]:hidden" forceMount>
            {visitedTabs.has('preview') && (
              <PreviewPane
                project={project}
                isProvisioning={isProvisioning}
                activeTab="preview"
                isChatHidden={false}
                onToggleChat={() => {}}
                historyEntries={historyEntries}
                processing={processing}
                groupedTodos={groupedTodos}
                renderOnlyActiveTab
                conversationMode={conversation.mode}
                onRemoveMobile={onRemoveMobile}
                hasSketch={!!sketchPane && !isManagedAgentProject}
                sketchPane={sketchPane}
              />
            )}
          </TabsContent>

          <TabsContent value="spec" className="h-full m-0 data-[state=inactive]:hidden" forceMount>
            {visitedTabs.has('spec') && (
              <PreviewPane
                project={project}
                isProvisioning={isProvisioning}
                activeTab="spec"
                isChatHidden={false}
                onToggleChat={() => {}}
                historyEntries={historyEntries}
                processing={processing}
                renderOnlyActiveTab
              />
            )}
          </TabsContent>

          <TabsContent value="history" className="h-full m-0 data-[state=inactive]:hidden" forceMount>
            {visitedTabs.has('history') && (
              <PreviewPane
                project={project}
                isProvisioning={isProvisioning}
                activeTab="history"
                isChatHidden={false}
                onToggleChat={() => {}}
                historyEntries={historyEntries}
                historyPagy={historyPagy}
                processing={processing}
                renderOnlyActiveTab
              />
            )}
          </TabsContent>

          <TabsContent value="integrations" className="h-full m-0 data-[state=inactive]:hidden" forceMount>
            {visitedTabs.has('integrations') && (
              <PreviewPane
                project={project}
                isProvisioning={isProvisioning}
                activeTab="integrations"
                isChatHidden={false}
                onToggleChat={() => {}}
                processing={processing}
                renderOnlyActiveTab
              />
            )}
          </TabsContent>

          {supportsLiveEnvironment && (
            <TabsContent value="live" className="h-full m-0 data-[state=inactive]:hidden" forceMount>
              {visitedTabs.has('live') && (
                <PreviewPane
                  project={project}
                  isProvisioning={isProvisioning}
                  activeTab="live"
                  isChatHidden={false}
                  onToggleChat={() => {}}
                  processing={processing}
                  renderOnlyActiveTab
                />
              )}
            </TabsContent>
          )}
        </div>
      </Tabs>
    </div>
  )
}
